import { Fragment } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  CreateButton,
  Loading,
  TopToolbar,
  useDataProvider,
  useGetList,
  useNotify,
  useRefresh,
  useRedirect,
} from 'react-admin';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { performStatusTransition } from '../actions/performStatusTransition';
import { marketsForScope } from '../dataProvider/types';
import type { AdminDataProvider, SliderSlide } from '../dataProvider';

/**
 * The marketing-site sliders surface (D-cms-sliders). Slides are GROUPED BY PLACEMENT (a
 * free CMS-owned key, e.g. 'homepage-hero'): each placement is its OWN sortable list,
 * ordered by the per-placement `order` integer. The reorder reuses the page-schema
 * composer's dnd-kit pattern (DndContext + SortableContext + verticalListSortingStrategy +
 * arrayMove + a useSortable card, plus accessible move up / down); a drag NEVER crosses
 * placements. Reordering rewrites the moved slides' `order` through the chokepoint
 * (cms:author), audited. OPERATIONAL market-scoped: each placement's list is the
 * scope-filtered slides (the active markets PLUS the all-market ones). An 'all'-market
 * slide is ONE shared row visible in both scopes at its one order position (marked 'all').
 * NO publish: the active toggle is the whole lifecycle (like testimonials).
 */

const marketLabel = (m: string) => (m === 'all' ? 'ALL' : m.toUpperCase());

const SortableSlideCard = ({
  slide,
  index,
  total,
  canAuthor,
  onMove,
  onToggle,
  onEdit,
}: {
  slide: SliderSlide;
  index: number;
  total: number;
  canAuthor: boolean;
  onMove: (dir: -1 | 1) => void;
  onToggle: () => void;
  onEdit: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: slide.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{ mb: 1 }}
      data-testid={`slide-card-${slide.id}`}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          {/* The drag handle (dnd), alongside the accessible move up / down. */}
          <Box
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', px: 0.5, color: 'text.secondary' }}
            data-testid={`slide-drag-${slide.id}`}
            aria-label="Drag to reorder"
          >
            ::
          </Box>
          <Box
            component="img"
            src={slide.imageUrl}
            alt=""
            sx={{
              width: 80,
              height: 48,
              objectFit: 'cover',
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          />
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {slide.cta.label || '(no label)'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {slide.cta.link || '(no link)'}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={marketLabel(slide.market)}
            color={slide.market === 'all' ? 'info' : 'default'}
            variant="outlined"
            data-testid={`slide-market-${slide.id}`}
          />
          <Chip
            size="small"
            label={slide.active ? 'Active' : 'Inactive'}
            color={slide.active ? 'success' : 'default'}
            variant={slide.active ? 'filled' : 'outlined'}
            data-testid={`slide-active-${slide.id}`}
          />
          {canAuthor && (
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                disabled={index === 0}
                onClick={() => onMove(-1)}
                data-testid={`slide-up-${slide.id}`}
              >
                Up
              </Button>
              <Button
                size="small"
                disabled={index === total - 1}
                onClick={() => onMove(1)}
                data-testid={`slide-down-${slide.id}`}
              >
                Down
              </Button>
              <Button
                size="small"
                onClick={onToggle}
                data-testid={`slide-toggle-${slide.id}`}
              >
                {slide.active ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                size="small"
                onClick={onEdit}
                data-testid={`slide-edit-${slide.id}`}
              >
                Edit
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const PlacementList = ({
  placement,
  slides,
  canAuthor,
  onReorder,
  onToggle,
  onEdit,
}: {
  placement: string;
  slides: SliderSlide[];
  canAuthor: boolean;
  onReorder: (placement: string, ordered: SliderSlide[]) => void;
  onToggle: (slide: SliderSlide) => void;
  onEdit: (slide: SliderSlide) => void;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const move = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= slides.length) return;
    onReorder(placement, arrayMove(slides, index, to));
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = slides.findIndex((s) => s.id === active.id);
    const to = slides.findIndex((s) => s.id === over.id);
    if (from === -1 || to === -1) return;
    onReorder(placement, arrayMove(slides, from, to));
  };

  return (
    <Box sx={{ mb: 3 }} data-testid={`placement-${placement}`}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {placement}
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={slides.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {slides.map((slide, index) => (
            <SortableSlideCard
              key={slide.id}
              slide={slide}
              index={index}
              total={slides.length}
              canAuthor={canAuthor}
              onMove={(dir) => move(index, dir)}
              onToggle={() => onToggle(slide)}
              onEdit={() => onEdit(slide)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

const SliderManagerActions = ({ canAuthor }: { canAuthor: boolean }) => (
  <TopToolbar>
    {canAuthor && <CreateButton resource="sliderSlides" label="New slide" />}
  </TopToolbar>
);

/** The manager itself. Exported for testing without the access wrapper. */
export const SliderManagerView = () => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { activeRegion } = useRegion();
  const { role, accessibleRegions } = useAccessContext();

  const canAuthor = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:author' },
  );

  // The active markets PLUS the all-market slides (a both slide shows in every scope).
  const inScope = [...marketsForScope(activeRegion ?? 'GLOBAL'), 'all'];
  const { data, isLoading } = useGetList<SliderSlide>('sliderSlides', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'order', order: 'ASC' },
    filter: { market: inScope },
  });

  if (isLoading) return <Loading />;

  const slides = data ?? [];
  // Group by placement; within each, order-sorted (the per-placement list).
  const placements = Array.from(new Set(slides.map((s) => s.placement))).sort();
  const byPlacement = (p: string) =>
    slides.filter((s) => s.placement === p).sort((a, b) => a.order - b.order);

  // Persist a placement's new sequence: reassign the SAME set of order integers (the
  // placement's filtered order values) to the new arrangement, writing back only the
  // slides whose order changed. Each write routes through the chokepoint (cms:author).
  const onReorder = async (placement: string, ordered: SliderSlide[]) => {
    const orderValues = byPlacement(placement).map((s) => s.order);
    try {
      for (let i = 0; i < ordered.length; i += 1) {
        const slide = ordered[i];
        const nextOrder = orderValues[i];
        if (slide.order !== nextOrder) {
          await performStatusTransition({
            dataProvider,
            role,
            capability: 'cms:author',
            resource: 'sliderSlides',
            id: slide.id,
            data: { order: nextOrder, updatedAt: new Date().toISOString() },
            previousData: slide,
          });
        }
      }
      refresh();
    } catch {
      notify('Could not reorder the slides', { type: 'error' });
    }
  };

  const onToggle = async (slide: SliderSlide) => {
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'cms:author',
        resource: 'sliderSlides',
        id: slide.id,
        data: { active: !slide.active, updatedAt: new Date().toISOString() },
        previousData: slide,
      });
      notify(slide.active ? 'Slide deactivated' : 'Slide activated', {
        type: 'success',
      });
      refresh();
    } catch {
      notify('Could not change the slide', { type: 'error' });
    }
  };

  const onEdit = (slide: SliderSlide) =>
    redirect('edit', 'sliderSlides', slide.id);

  return (
    <Box data-testid="slider-manager">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" component="h1">
          Sliders
        </Typography>
        <SliderManagerActions canAuthor={canAuthor} />
      </Stack>
      {placements.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No slides in this region yet.
        </Typography>
      ) : (
        placements.map((p) => (
          <Fragment key={p}>
            <PlacementList
              placement={p}
              slides={byPlacement(p)}
              canAuthor={canAuthor}
              onReorder={onReorder}
              onToggle={onToggle}
              onEdit={onEdit}
            />
          </Fragment>
        ))
      )}
    </Box>
  );
};

/** Gated entry point. ROLE-gated to editor and super_admin. */
export const SliderManager = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <SliderManagerView />
  </RequireAccess>
);
